import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Activity,
  Bell,
  BookOpen,
  Boxes,
  BriefcaseBusiness,
  ChartNoAxesCombined,
  ChevronRight,
  CircleUserRound,
  CloudSun,
  Database,
  Earth,
  FolderKanban,
  Globe2,
  Layers3,
  Map,
  Menu,
  Radar,
  Search,
  Settings,
  ShieldAlert,
  SlidersHorizontal,
  Sparkles,
  Truck,
  X,
} from 'lucide-react';

const navItems = [
  { to: '/', label: 'Overview', icon: Earth },
  { to: '/map', label: 'Map', icon: Map },
  { to: '/layers', label: 'Layers', icon: Layers3 },
  { to: '/events', label: 'Events', icon: Radar },
  { to: '/analytics', label: 'Analytics', icon: ChartNoAxesCombined },
  { to: '/forecast', label: 'Forecast', icon: CloudSun },
  { to: '/infrastructure', label: 'Infrastructure', icon: Boxes },
  { to: '/mobility', label: 'Mobility', icon: Truck },
  { to: '/crises', label: 'Crisis Monitor', icon: ShieldAlert },
  { to: '/projects', label: 'Projects', icon: FolderKanban },
  { to: '/catalog', label: 'Data Catalog', icon: Database },
  { to: '/search', label: 'Search & Discovery', icon: Search },
  { to: '/settings', label: 'Settings', icon: Settings },
];

const routeTitles: Record<string, string> = {
  '/': '1. Global Overview Dashboard',
  '/map': '2. 3D Earth View',
  '/crises': '3. Conflicts & Crisis Monitor',
  '/infrastructure': '4. Infrastructure Overview',
  '/mobility': '5. Real-Time Mobility',
  '/forecast': '6. Forecast & Simulation',
  '/analytics': '7. Data & Analytics',
  '/events': '8. Timeline & Events',
  '/search': '9. Search & Discovery',
  '/layers': 'Layer Catalog',
  '/projects': 'Projects Portfolio',
  '/catalog': 'Data Catalog',
  '/settings': 'Settings',
};

export function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const [clock, setClock] = useState(new Date());
  const [mobileNav, setMobileNav] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [timeZone, setTimeZone] = useState(() => readPreferredTimeZone());

  useEffect(() => {
    const timer = window.setInterval(() => setClock(new Date()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    const refreshPreferences = () => setTimeZone(readPreferredTimeZone());
    window.addEventListener('storage', refreshPreferences);
    window.addEventListener('world-twin-preferences', refreshPreferences);
    return () => {
      window.removeEventListener('storage', refreshPreferences);
      window.removeEventListener('world-twin-preferences', refreshPreferences);
    };
  }, []);


  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSearchOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, []);

  useEffect(() => {
    setMobileNav(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const title = routeTitles[location.pathname] ?? 'World Twin';
  const time = useMemo(
    () => new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone }).format(clock),
    [clock, timeZone],
  );
  const date = useMemo(
    () => new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone }).format(clock),
    [clock, timeZone],
  );

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = query.trim();
    navigate(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : '/search');
  };

  return (
    <div className="app-shell">
      <aside className={`sidebar ${mobileNav ? 'is-open' : ''}`}>
        <div className="brand">
          <div className="brand__mark"><Globe2 size={31} /></div>
          <div>
            <strong>WORLD TWIN</strong>
            <small>Digital Twin of the Planet</small>
          </div>
          <button type="button" className="sidebar__close" onClick={() => setMobileNav(false)} aria-label="Close navigation"><X size={20} /></button>
        </div>
        <nav className="sidebar__nav" aria-label="Primary navigation">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} end={to === '/'} className={({ isActive }) => `nav-item ${isActive ? 'is-active' : ''}`}>
              <Icon size={19} />
              <span>{label}</span>
              <ChevronRight size={15} className="nav-item__arrow" />
            </NavLink>
          ))}
        </nav>
        <div className="sidebar__status">
          <span className="live-dot" />
          <span>System Operational</span>
        </div>
        <button type="button" className="profile-card" onClick={() => navigate('/settings')}>
          <span className="profile-card__avatar"><CircleUserRound size={30} /></span>
          <span><strong>Admin</strong><small>Super Administrator</small></span>
          <ChevronRight size={16} />
        </button>
      </aside>

      <div className="shell-main">
        <header className="topbar">
          <button type="button" className="mobile-menu" onClick={() => setMobileNav(true)} aria-label="Open navigation"><Menu size={21} /></button>
          <h1>{title}</h1>
          <form className="topbar-search" onSubmit={submitSearch}>
            <Search size={17} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} onFocus={() => setSearchOpen(true)} placeholder="Search anything…" aria-label="Global search" />
          </form>
          <div className="topbar-actions">
            <button type="button" className="icon-action has-alert" aria-label="Notifications" onClick={() => navigate('/events')}><Bell size={19} /></button>
            <button type="button" className="icon-action has-alert" aria-label="System health" onClick={() => navigate('/catalog')}><Activity size={19} /></button>
            <button type="button" className="icon-action" aria-label="Quick settings" onClick={() => navigate('/settings')}><Settings size={19} /></button>
            <div className="topbar-clock"><strong>{time} {formatTimeZoneLabel(timeZone)}</strong><small>{date}</small></div>
            <button type="button" className="view-action" onClick={() => { navigate(location.pathname); setQuery(''); }}><BriefcaseBusiness size={16} /><span>Default View</span><SlidersHorizontal size={14} /></button>
          </div>
        </header>
        <main className="page-stage"><Outlet /></main>
      </div>

      {mobileNav && <button type="button" className="nav-backdrop" onClick={() => setMobileNav(false)} aria-label="Close navigation" />}

      {searchOpen && (
        <div className="command-overlay" role="presentation" onMouseDown={() => setSearchOpen(false)}>
          <div className="command-palette" role="dialog" aria-label="Search commands" onMouseDown={(event) => event.stopPropagation()}>
            <form onSubmit={submitSearch} className="command-palette__input">
              <Search size={18} />
              <input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search places, events, datasets and organizations…" />
              <kbd>ESC</kbd>
            </form>
            <div className="command-palette__section"><span>Suggested</span></div>
            {['Power plants in Europe', 'Cyclone events this month', 'Air quality in India', 'Critical infrastructure in Brazil'].map((item) => (
              <button type="button" key={item} className="command-result" onClick={() => { setQuery(item); navigate(`/search?q=${encodeURIComponent(item)}`); }}>
                <Sparkles size={16} /><span>{item}</span><small>Explore globally</small>
              </button>
            ))}
            <div className="command-palette__footer"><BookOpen size={15} /> Press Enter to search the federated catalog</div>
          </div>
        </div>
      )}
    </div>
  );
}

function readPreferredTimeZone(): string {
  if (typeof window === 'undefined') return 'UTC';
  try {
    const value = JSON.parse(window.localStorage.getItem('world-twin.preferences') ?? '{}') as { timezone?: unknown };
    return value.timezone === 'America/Sao_Paulo' || value.timezone === 'Europe/London' || value.timezone === 'UTC' ? value.timezone : 'UTC';
  } catch {
    return 'UTC';
  }
}

function formatTimeZoneLabel(timeZone: string): string {
  if (timeZone === 'UTC') return 'UTC';
  return timeZone.split('/').at(-1)?.replaceAll('_', ' ') ?? timeZone;
}
